import { db } from '$db/local/db';
import { getSupabase } from '$db/remote/supabase';
import { getActiveUserId } from '$core/identity/active-user';
import { DEFAULT_PREFERENCES } from '$core/preferences/types';

const AUTH_KEY = 'sq:auth';
const PREFS_KEY = 'sq:preferences';

export interface PurgeResult {
  remoteErrors: Array<{ table: string; error: unknown }>;
  sessionPreserved: boolean;
}

/**
 * Borra todos los datos del usuario actual sin cerrar la sesión:
 *   1. Purga las 22 tablas remotas de Supabase (filtradas por `user_id`).
 *   2. Borra IndexedDB local (Dexie).
 *   3. Resetea `sq:preferences` a los defaults (theme = wine).
 *   4. PRESERVA `sq:auth` — el usuario sigue logueado tras el reload.
 */
export async function purgeAllLocalData(): Promise<PurgeResult> {
  const userId = getActiveUserId();
  const remoteErrors: Array<{ table: string; error: unknown }> = [];

  const supabase = getSupabase();
  const isAuthenticated =
    typeof userId === 'string' && userId.length > 0 && !userId.startsWith('guest:');
  
  if (supabase && isAuthenticated) {
    try {
      // Llamada atómica a la función RPC que borra todos los datos del usuario
      const { error } = await supabase.rpc('delete_user_data');
      if (error) {
        remoteErrors.push({ table: 'rpc:delete_user_data', error });
        console.error('[purge] RPC delete_user_data failed:', error);
        // Si el borrado remoto falla, NO recargamos la página.
        // Devolvemos el error para que la UI pueda mostrar un mensaje.
        return { remoteErrors, sessionPreserved: false };
      }
    } catch (err) {
      remoteErrors.push({ table: 'rpc:delete_user_data', error: err });
      console.error('[purge] RPC delete_user_data threw:', err);
      return { remoteErrors, sessionPreserved: false };
    }
  }

  try {
    // En lugar de destruir la base de datos (lo que rompe las consultas reactivas
    // de Svelte y causa el error removeChild), vaciamos todas las tablas dentro
    // de una transacción atómica. La base de datos sigue existiendo, pero sin datos.
    if (db.isOpen()) {
      await db.transaction('rw', db.tables, async () => {
        for (const table of db.tables) {
          await table.clear();
        }
      });
    }
  } catch (err) {
    console.warn('[purge] local table clear failed:', err);
  }

  let sessionPreserved = false;
  if (typeof localStorage !== 'undefined') {
    const authRaw = localStorage.getItem(AUTH_KEY);
    sessionPreserved = authRaw !== null;

    localStorage.clear();

    if (authRaw !== null) localStorage.setItem(AUTH_KEY, authRaw);
    localStorage.setItem(PREFS_KEY, JSON.stringify(DEFAULT_PREFERENCES));
    
    // Guardamos localmente que acabamos de borrar para evitar bucles de auto-borrado
    localStorage.setItem('sq:last_seen_wipe_at', String(Date.now()));
  }

  if (typeof location !== 'undefined') {
    location.reload();
  }

  return { remoteErrors, sessionPreserved };
}